import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import './css/popup.css';
import MapTilePrinter from './MapTilePrinter';
import { field } from 'geomag';
import { QRCodeCanvas } from 'qrcode.react';
import Draggable from 'react-draggable';
import { adjustLineWidthsForPrint } from './printLineDensity';
import { Translator, resources } from './Translator';
import Marker from './Marker';
import { MARKER_ARRAY } from './constants';
import { CommonUtils } from './CommonUtils';
import { generateGridGeoJSON } from './mgrsUtmGrid';
import { exportGeoPdf } from './GeoPdfExporter';
import { exportGeoTiff } from './GeoTiffExporter';

const translator = new Translator(resources);

const PAPER_SIZES = {
	A3: { width: 297, height: 400 },//420
	A4: { width: 210, height: 277 },//297
	B4: { width: 257, height: 344 },//364
	B5: { width: 182, height: 237 },//257
};

const SCALES = [
	{ label: '1:10,000',  value: 10000  },
	{ label: '1:12,500',  value: 12500  },
	{ label: '1:25,000',  value: 25000  },
	{ label: '1:50,000',  value: 50000  },
	{ label: '1:100,000', value: 100000 },
	{ label: '1:200,000', value: 200000 },
	{ label: '任意',      value: 'custom' },
];

const CUSTOM_SCALE_MIN = 500;
const CUSTOM_SCALE_MAX = 200000;

const MAGNETIC_LINE_COLORS = [
	{ value: 'red',    label: 'ColorRed',    color: '#ff0000' },
	{ value: 'black',  label: 'ColorBlack',  color: '#000000' },
	{ value: 'blue',   label: 'ColorBlue',   color: '#0000ff' },
	{ value: 'green',  label: 'ColorGreen',  color: '#008000' },
	{ value: 'purple', label: 'ColorPurple', color: '#800080' },
];

// 印刷ダイアログの幅 (px)
const POPUP_MIN_WIDTH = 260;
const POPUP_MAX_WIDTH_TWO_COL = 480;
const POPUP_MAX_WIDTH_ONE_COL = 320;

const SOURCE_ID     = 'print-area';
const LAYER_FILL_ID = 'print-area-fill';
const LAYER_LINE_ID = 'print-area-line';

const PrintMapPopup = ({ map, markers, title, ownerName, memo, onClose }) => {
	const t = (key) => translator.t(key);
	const LS = 'printMapSettings';
	const lsDefaults = { paperSize: 'A4', orientation: 'landscape', scale: 25000, lineCount: 6, scaleBarPos: 'bottom-center', colorMode: 'color', textSizeMode: 'medium', magneticLineColor: 'red', markerMode: 'normal', markerSizeMode: 'medium', qrPos: 'br' };
	const lsSaved = (() => { try { return JSON.parse(localStorage.getItem(LS)) || {}; } catch { return {}; } })();
	const lsGet = (key) => lsSaved[key] ?? lsDefaults[key];

	const [paperSize, setPaperSize]       = useState(() => lsGet('paperSize'));
	const [orientation, setOrientation]   = useState(() => lsGet('orientation'));
	const [scale, setScale]               = useState(() => lsGet('scale'));
	const [isCustomScale, setIsCustomScale] = useState(() => !SCALES.some(s => s.value === lsGet('scale')));
	const [customScaleInput, setCustomScaleInput] = useState(() => {
		const saved = lsGet('scale');
		return SCALES.some(s => s.value === saved) ? '' : String(saved);
	});
	const [isPrinting, setIsPrinting]     = useState(false);
	const [progress, setProgress]         = useState(0);
	const [lineCount, setLineCount]       = useState(() => lsGet('lineCount'));
	const [scaleBarPos, setScaleBarPos]   = useState(() => lsGet('scaleBarPos'));
	const [colorMode, setColorMode]       = useState(() => lsGet('colorMode'));
	const [textSizeMode, setTextSizeMode] = useState(() => lsGet('textSizeMode') || 'medium');
	const [magneticLineColor, setMagneticLineColor] = useState(() => lsGet('magneticLineColor'));
	// 'normal' | 'none' | 'redDiamond'
	const [markerMode, setMarkerMode] = useState(() => lsGet('markerMode'));
	// 'small' (0.75x) | 'medium' (1.0x) | 'large' (1.5x)
	const [markerSizeMode, setMarkerSizeMode] = useState(() => lsGet('markerSizeMode'));
	const [qrPos, setQrPos] = useState(() => lsGet('qrPos'));
	// 入力項目の折りたたみ状態
	const [isCollapsed, setIsCollapsed] = useState(false);
	// 折りたたみ/展開時、変化前のダイアログ上端を保持して位置を合わせる
	const preToggleTopRef = useRef(null);
	const handleToggleCollapse = () => {
		const el = popupNodeRef.current;
		if (el) preToggleTopRef.current = el.getBoundingClientRect().top;
		setIsCollapsed(v => !v);
	};

	// 印刷ダイアログの表示位置を localStorage に永続化
	const POS_LS = 'printMapPopupPos';
	const [popupPos, setPopupPos] = useState(() => {
		try {
			const v = JSON.parse(localStorage.getItem(POS_LS));
			if (v && typeof v.x === 'number' && typeof v.y === 'number') return v;
		} catch {}
		return { x: 0, y: 0 };
	});
	const popupNodeRef = useRef(null);
	// タイトルバーが画面内に最低限見える状態か判定
	// 閾値: タイトルバー上端 ~30px 以上を画面内にとどめる
	const TITLEBAR_VISIBLE_PX = 40;
	const isTitleBarVisible = (rect) => {
		const { innerWidth, innerHeight } = window;
		return (
			rect.top >= -2 &&
			rect.top <= innerHeight - TITLEBAR_VISIBLE_PX &&
			rect.right >= TITLEBAR_VISIBLE_PX &&
			rect.left <= innerWidth - TITLEBAR_VISIBLE_PX
		);
	};
	// タイトルバーが画面外なら、上端に揃えて x はクランプした位置を返す
	const clampToTop = (rect, currentPos) => {
		const { innerWidth } = window;
		// 上端 0 にするには、現在のオフセットから rect.top を差し引く
		const newY = currentPos.y - rect.top;
		// x は最低 TITLEBAR_VISIBLE_PX が画面内に残るようクランプ
		let newX = currentPos.x;
		const minLeft = TITLEBAR_VISIBLE_PX - rect.width; // これより左に行くと右端が画面左外
		const maxLeft = innerWidth - TITLEBAR_VISIBLE_PX;  // これより右に行くと左端が画面右外
		const desiredLeftMin = minLeft + rect.width / 2 - rect.width / 2; // = minLeft
		// 現在の左端と整合する new x を計算: newLeft = rect.left + (newX - currentPos.x)
		// なので newLeft が範囲 [desiredLeftMin, maxLeft] に収まるよう newX を調整
		const currentLeft = rect.left;
		const dx = (currentLeft < desiredLeftMin)
			? desiredLeftMin - currentLeft
			: (currentLeft > maxLeft ? maxLeft - currentLeft : 0);
		newX = currentPos.x + dx;
		return { x: newX, y: newY };
	};
	// 折りたたみ前後でダイアログ上端の位置を維持する
	useLayoutEffect(() => {
		if (preToggleTopRef.current == null) return;
		const el = popupNodeRef.current;
		if (!el) { preToggleTopRef.current = null; return; }
		const newTop = el.getBoundingClientRect().top;
		const delta = preToggleTopRef.current - newTop;
		preToggleTopRef.current = null;
		if (Math.abs(delta) < 0.5) return;
		setPopupPos(prev => {
			const next = { x: prev.x, y: prev.y + delta };
			try { localStorage.setItem(POS_LS, JSON.stringify(next)); } catch {}
			return next;
		});
	}, [isCollapsed]);

	// 表示後にタイトルバーが画面外なら画面上端に戻す
	useEffect(() => {
		const el = popupNodeRef.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		if (!isTitleBarVisible(r)) {
			const next = clampToTop(r, popupPos);
			setPopupPos(next);
			try { localStorage.setItem(POS_LS, JSON.stringify(next)); } catch {}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const onDragStop = (_e, data) => {
		const next = { x: data.x, y: data.y };
		const el = popupNodeRef.current;
		if (el) {
			const r = el.getBoundingClientRect();
			if (!isTitleBarVisible(r)) {
				// ドラッグ後にタイトルバーが画面外に出ていたら画面上端に強制復帰
				const fixed = clampToTop(r, next);
				setPopupPos(fixed);
				try { localStorage.setItem(POS_LS, JSON.stringify(fixed)); } catch {}
				return;
			}
		}
		setPopupPos(next);
		try { localStorage.setItem(POS_LS, JSON.stringify(next)); } catch {}
	};

	// デフォルトは2列レイアウト。横幅が極端に狭い時のみ1列にフォールバック
	const canFitTwoCol = () => typeof window !== 'undefined' && window.innerWidth >= 340;
	const [isTwoCol, setIsTwoCol] = useState(() => canFitTwoCol());
	useEffect(() => {
		const onResize = () => {
			setIsTwoCol(canFitTwoCol());
		};
		window.addEventListener('resize', onResize);
		window.addEventListener('orientationchange', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
			window.removeEventListener('orientationchange', onResize);
		};
	}, []);

	// 設定変更時に localStorage へ保存
	useEffect(() => {
		try { localStorage.setItem(LS, JSON.stringify({ paperSize, orientation, scale, lineCount, scaleBarPos, colorMode, textSizeMode, magneticLineColor, markerMode, markerSizeMode, qrPos })); } catch {}
	}, [paperSize, orientation, scale, lineCount, scaleBarPos, colorMode, textSizeMode, magneticLineColor, markerMode, markerSizeMode, qrPos]);
	// キャプチャ完了後、popup blocker でブロックされた場合に BlobURL を保持
	const [pendingBlobUrl, setPendingBlobUrl] = useState(null);
	// 保存メニュー (PDF保存 / GeoTIFF保存) の開閉
	const [showSaveMenu, setShowSaveMenu] = useState(false);
	const saveMenuWrapRef = useRef(null);
	// 印刷範囲の中心（地図中心とは独立してドラッグ移動可能）
	const [printCenter, setPrintCenter] = useState(null);
	const savedViewRef  = useRef(null);
	const isDraggingRef = useRef(false);
	const dragLngLatRef = useRef(null);
	const qrCanvasRef   = useRef(null);

	// QRコード用URL: 印刷範囲の中心座標・zoom=14 で生成
	const qrUrl = (() => {
		const base = window.location.href.replace('http://localhost:3000/', 'https://collabomap.com/');
		const center = printCenter || (map && map.getCenter());
		if (!center) return base.replace(/#.*$/, '');
		const urlWithoutHash = base.replace(/#.*$/, '');
		return `${urlWithoutHash}#14/${center.lat.toFixed(5)}/${center.lng.toFixed(5)}`;
	})();

	// マウント時に印刷中心を地図中心で初期化
	useEffect(() => {
		if (map) setPrintCenter(c => c || map.getCenter());
	}, [map]);

	// 保存メニューの外側クリック/タップで閉じる
	useEffect(() => {
		if (!showSaveMenu) return;
		const onDocDown = (e) => {
			if (saveMenuWrapRef.current && !saveMenuWrapRef.current.contains(e.target)) {
				setShowSaveMenu(false);
			}
		};
		document.addEventListener('mousedown', onDocDown);
		document.addEventListener('touchstart', onDocDown);
		return () => {
			document.removeEventListener('mousedown', onDocDown);
			document.removeEventListener('touchstart', onDocDown);
		};
	}, [showSaveMenu]);

	// 用紙の実際の幅・高さ(mm)を取得
	const getPaperDimensions = useCallback(() => {
		const paper = PAPER_SIZES[paperSize];
		if (orientation === 'landscape') {
			return { widthMm: paper.height, heightMm: paper.width };
		}
		return { widthMm: paper.width, heightMm: paper.height };
	}, [paperSize, orientation]);

	// 印刷範囲の地理的境界を計算
	const calculateBounds = useCallback(() => {
		if (!map) return null;
		const center = printCenter || map.getCenter();
		const { widthMm, heightMm } = getPaperDimensions();
		const widthMeters  = (widthMm  / 1000) * scale;
		const heightMeters = (heightMm / 1000) * scale;
		const latRad   = center.lat * Math.PI / 180;
		const deltaLat = heightMeters / 111111;
		const deltaLng = widthMeters  / (111111 * Math.cos(latRad));
		return {
			west:  center.lng - deltaLng / 2,
			east:  center.lng + deltaLng / 2,
			south: center.lat - deltaLat / 2,
			north: center.lat + deltaLat / 2,
		};
	}, [map, getPaperDimensions, scale, printCenter]);

	// 地図上の印刷範囲矩形を更新
	const updatePrintArea = useCallback(() => {
		if (!map) return;
		const bounds = calculateBounds();
		if (!bounds) return;
		const geojson = {
			type: 'Feature',
			geometry: {
				type: 'Polygon',
				coordinates: [[
					[bounds.west, bounds.south],
					[bounds.east, bounds.south],
					[bounds.east, bounds.north],
					[bounds.west, bounds.north],
					[bounds.west, bounds.south],
				]],
			},
		};
		if (map.getSource(SOURCE_ID)) {
			map.getSource(SOURCE_ID).setData(geojson);
		} else {
			map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });
			map.addLayer({
				id: LAYER_FILL_ID,
				type: 'fill',
				source: SOURCE_ID,
				paint: { 'fill-color': '#3399cc', 'fill-opacity': 0.12 },
			});
			map.addLayer({
				id: LAYER_LINE_ID,
				type: 'line',
				source: SOURCE_ID,
				paint: { 'line-color': '#3399cc', 'line-width': 2, 'line-dasharray': [5, 3] },
			});
		}
	}, [map, calculateBounds]);

	// 印刷範囲枠のドラッグ移動
	useEffect(() => {
		if (!map) return;

		const onEnter = () => { map.getCanvas().style.cursor = 'grab'; };
		const onLeave = () => { if (!isDraggingRef.current) map.getCanvas().style.cursor = ''; };

		const onDown = (e) => {
			// マーカー / ラベルの移動モード中は印刷範囲を動かさない。
			// どちらも移動中の要素に .blinking-marker クラスを付与するので
			// それを統一フラグとして使う。
			if (document.querySelector('.blinking-marker')) return;
			isDraggingRef.current = true;
			dragLngLatRef.current = e.lngLat;
			map.dragPan.disable();
			map.getCanvas().style.cursor = 'grabbing';
		};

		const onMove = (e) => {
			if (!isDraggingRef.current || !dragLngLatRef.current) return;
			const dLng = e.lngLat.lng - dragLngLatRef.current.lng;
			const dLat = e.lngLat.lat - dragLngLatRef.current.lat;
			dragLngLatRef.current = e.lngLat;
			setPrintCenter(c => {
				const base = c || map.getCenter();
				return { lat: base.lat + dLat, lng: base.lng + dLng };
			});
		};

		const onUp = () => {
			if (!isDraggingRef.current) return;
			isDraggingRef.current = false;
			dragLngLatRef.current = null;
			map.dragPan.enable();
			map.getCanvas().style.cursor = '';
		};

		map.on('mouseenter', LAYER_FILL_ID, onEnter);
		map.on('mouseleave', LAYER_FILL_ID, onLeave);
		map.on('mousedown',  LAYER_FILL_ID, onDown);
		map.on('mousemove',               onMove);
		map.on('mouseup',                 onUp);
		// タッチ対応（スマホ）
		map.on('touchstart', LAYER_FILL_ID, onDown);
		map.on('touchmove',                 onMove);
		map.on('touchend',                  onUp);
		map.on('touchcancel',               onUp);

		return () => {
			map.off('mouseenter', LAYER_FILL_ID, onEnter);
			map.off('mouseleave', LAYER_FILL_ID, onLeave);
			map.off('mousedown',  LAYER_FILL_ID, onDown);
			map.off('mousemove',               onMove);
			map.off('mouseup',                 onUp);
			map.off('touchstart', LAYER_FILL_ID, onDown);
			map.off('touchmove',                 onMove);
			map.off('touchend',                  onUp);
			map.off('touchcancel',               onUp);
			if (isDraggingRef.current) {
				map.dragPan.enable();
				isDraggingRef.current = false;
			}
			map.getCanvas().style.cursor = '';
		};
	}, [map]);

	// 印刷範囲矩形を削除
	const removePrintArea = useCallback(() => {
		if (!map) return;
		if (map.getLayer(LAYER_LINE_ID)) map.removeLayer(LAYER_LINE_ID);
		if (map.getLayer(LAYER_FILL_ID)) map.removeLayer(LAYER_FILL_ID);
		if (map.getSource(SOURCE_ID))    map.removeSource(SOURCE_ID);
	}, [map]);

	// 設定変更時に地図上の矩形を更新
	useEffect(() => {
		updatePrintArea();
	}, [updatePrintArea]);

	// アンマウント時に矩形を削除・ビューを復元
	useEffect(() => {
		return () => {
			removePrintArea();
			if (savedViewRef.current && map) {
				map.setCenter(savedViewRef.current.center);
				map.setZoom(savedViewRef.current.zoom);
			}
		};
	}, [removePrintArea, map]);

	// pendingBlobUrl のクリーンアップ
	useEffect(() => {
		return () => {
			if (pendingBlobUrl) URL.revokeObjectURL(pendingBlobUrl);
		};
	}, [pendingBlobUrl]);

	// Web Mercator の Y 座標（MapTilePrinter と同じ計算式）
	const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));

	// 磁北線を出力キャンバスに描画
	// bounds: 印刷地理範囲、count: 本数(2-12)
	const drawMagneticLines = (ctx, outW, outH, bounds, count, strokeColor = '#000000') => {
		console.log('[MagneticLines] called', { outW, outH, bounds, count });
		if (!count || count < 2) {
			console.log('[MagneticLines] skipped: count=', count);
			return;
		}
		count += 2;//2本たさないと指定した本数にならない
		const centerLat = (bounds.north + bounds.south) / 2;
		const centerLng = (bounds.east  + bounds.west)  / 2;
		let geomagResult;
		try {
			geomagResult = field(centerLat, centerLng);
		} catch (e) {
			console.error('[MagneticLines] geomag field() error:', e);
			return;
		}
		const declDeg = geomagResult.declination;
		const declRad = declDeg * Math.PI / 180;
		console.log('[MagneticLines] centerLat:', centerLat, 'centerLng:', centerLng, 'declDeg:', declDeg);

		// Canvas座標系: x=右(東) y=下(南)
		// 磁北方向ベクトル: (sin(δ), -cos(δ))
		const dx =  Math.sin(declRad);
		const dy = -Math.cos(declRad);
		// 磁北に対する垂直方向: (cos(δ), sin(δ))
		const px = Math.cos(declRad);
		const py = Math.sin(declRad);

		// 4隅を垂直方向に射影してカバー範囲を決定
		// 角ちょうどの線は valid < 2 になるため 1px 内側に縮める
		const projs = [[0,0],[outW,0],[0,outH],[outW,outH]].map(([x,y]) => x*px + y*py);
		const pMin = Math.min(...projs) + 1;
		const pMax = Math.max(...projs) - 1;
		console.log('[MagneticLines] dx:', dx, 'dy:', dy, 'pMin:', pMin, 'pMax:', pMax);

		ctx.save();
		ctx.strokeStyle = strokeColor;
		ctx.lineWidth   = 2;
		ctx.globalAlpha = 0.75;

		for (let i = 0; i < count; i++) {
			const p  = pMin + i * (pMax - pMin) / (count - 1);
			const rx = p * px;
			const ry = p * py;

			// キャンバス境界との交点を t パラメータで求める
			const tVals = [];
			if (Math.abs(dx) > 1e-10) { tVals.push(-rx / dx); tVals.push((outW - rx) / dx); }
			if (Math.abs(dy) > 1e-10) { tVals.push(-ry / dy); tVals.push((outH - ry) / dy); }

			const M = 0.5; // margin
			const valid = tVals.filter(t => {
				const x = rx + t*dx, y = ry + t*dy;
				return x >= -M && x <= outW+M && y >= -M && y <= outH+M;
			});
			console.log(`[MagneticLines] line ${i}: p=${p.toFixed(1)} rx=${rx.toFixed(1)} ry=${ry.toFixed(1)} tVals=${tVals.map(v=>v.toFixed(1))} valid=${valid.length}`);
			if (valid.length < 2) {
				console.warn(`[MagneticLines] line ${i}: skip (valid < 2)`);
				continue;
			}
			valid.sort((a,b) => a-b);
			const t0 = valid[0], t1 = valid[valid.length - 1];
			const x0 = rx + t0*dx, y0 = ry + t0*dy;
			const x1 = rx + t1*dx, y1 = ry + t1*dy;
			console.log(`[MagneticLines] line ${i}: (${x0.toFixed(0)},${y0.toFixed(0)}) → (${x1.toFixed(0)},${y1.toFixed(0)})`);

			ctx.beginPath();
			ctx.moveTo(x0, y0);
			ctx.lineTo(x1, y1);
			ctx.stroke();
		}
		ctx.restore();
		console.log('[MagneticLines] done');
	};

	// MGRS/UTM 座標グリッドを出力キャンバスに描画
	//   renderedBounds: capture() が返した実際の描画地理範囲 { west, east, north, south }
	//   マーカー描画と同じ Mercator Y ベースで緯度経度→ピクセル変換する
	const drawMgrsUtmGrid = (ctx, outW, outH, renderedBounds, targetDpi, textSizeScale = 1) => {
		const settings = CommonUtils.gridOverlay || CommonUtils.GRID_OVERLAY_DEFAULT;
		if (!settings || (settings.mode !== 'mgrs' && settings.mode !== 'utm')) return;
		if (!renderedBounds) return;

		const { west, east, north, south } = renderedBounds;
		const lngSpan = east - west;
		const nY = mercY(north);
		const sY = mercY(south);
		const mYSpan = nY - sY;
		if (lngSpan <= 0 || mYSpan <= 0) return;

		const toPx = (lng, lat) => [
			(lng - west) / lngSpan * outW,
			(nY - mercY(lat)) / mYSpan * outH,
		];

		const bounds = { north, south, east, west };
		const { lines, labels } = generateGridGeoJSON(bounds, settings.mode, {
			color: settings.color,
			haloColor: '#ffffff',
			width: settings.width,
			opacity: settings.opacity,
			// 印刷は 300dpi。画面相当(約96dpi)の本数になるよう論理ピクセル幅に換算
			viewportPx: outW / (targetDpi / 96),
		});

		ctx.save();
		ctx.strokeStyle = settings.color || '#dd5144';
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';

		// 主線/補助線で太さ・透明度を変える（feature プロパティ基準）
		for (const f of lines) {
			const p = f.properties || {};
			ctx.globalAlpha = (p.opacity != null) ? p.opacity : ((settings.opacity != null) ? settings.opacity : 0.7);
			ctx.lineWidth = Math.max(1, (p.width || settings.width || 1) * 0.2 / 25.4 * targetDpi);
			const coords = f.geometry.coordinates;
			ctx.beginPath();
			for (let i = 0; i < coords.length; i++) {
				const [x, y] = toPx(coords[i][0], coords[i][1]);
				if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
			}
			ctx.stroke();
		}
		ctx.restore();

		// ラベル（ハロー付き）。globalAlpha は不透明にして可読性を確保
		ctx.save();
		ctx.lineJoin = 'round';
		for (const f of labels) {
			const [lng, lat] = f.geometry.coordinates;
			const [x, y] = toPx(lng, lat);
			if (x < -50 || x > outW + 50 || y < -50 || y > outH + 50) continue;
			const props = f.properties;
			// 座標番号は線の上・中央。100km ID はセル左下隅(左下基準)に描画。
			if (props.gridSquareLabel) {
				ctx.textAlign = 'left';
				ctx.textBaseline = 'bottom';
			} else {
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
			}
			// fontSize=20 → 約5mm 相当（ユーザーラベルと同じ基準）
			const fontSizeMm = (props.fontSize || 14) / 20 * 5;
			const outFontSize = fontSizeMm / 25.4 * targetDpi * textSizeScale;
			ctx.font = `bold ${outFontSize}px sans-serif`;
			ctx.strokeStyle = props.haloColor || '#ffffff';
			ctx.lineWidth = outFontSize * 0.18;
			ctx.strokeText(props.label || '', x, y);
			ctx.fillStyle = props.color || settings.color || '#dd5144';
			ctx.fillText(props.label || '', x, y);
		}
		ctx.restore();
	};

	// スケールバーの全長と区切りを「暗算しやすい」値に整える
	//  - 全長 (total)        : {1, 5} × 10ⁿ 系列 (10, 50, 100, 500, 1000, ...)
	//  - 区切り間隔 (tick)   : {1, 2, 5} × 10ⁿ 系列 (1, 2, 5, 10, 20, 50, ...)
	//  - 区切り数 (count)    : 1..5、最大 5
	//  - 全長は用紙幅の 80% 以内に収まる最大値を採用 (はみ出し防止)
	const computeScale = (outW, scaleDenom, targetDpi) => {
		const realMetersPerPx = (25.4 / targetDpi) * scaleDenom / 1000;
		const maxTotalMeters  = Math.max(outW * 0.8 * realMetersPerPx, 1e-9);

		// Step 1: maxTotalMeters 以下の最大の {1, 5} × 10ⁿ を選ぶ
		const expHi = Math.floor(Math.log10(maxTotalMeters));
		let total = 1;
		outer: for (let e = expHi + 1; e >= expHi - 10; e--) {
			const base = Math.pow(10, e);
			for (const m of [5, 1]) {                // 大きい方から
				const cand = m * base;
				if (cand <= maxTotalMeters) { total = cand; break outer; }
			}
		}

		// Step 2: total/tick が 1..5 の整数になる、最小の {1, 2, 5} × 10ⁿ を tick に
		let tick = total;
		const TICK_MANTISSA = [1, 2, 5];
		const expLo = Math.floor(Math.log10(total));
		search: for (let e = expLo - 10; e <= expLo; e++) {
			const base = Math.pow(10, e);
			for (const m of TICK_MANTISSA) {
				const t = m * base;
				const q = total / t;
				if (q >= 1 && q <= 5 && Math.abs(q - Math.round(q)) < 1e-9) {
					tick = t;
					break search;
				}
			}
		}
		const count = Math.max(1, Math.round(total / tick));
		return { tick, count, total };
	};

	// スケールバーを出力キャンバスに描画
	// pos: 'top-left'|'top-center'|'top-right'|'bottom-left'|'bottom-center'|'bottom-right'
	const drawScaleBar = (ctx, outW, outH, scaleDenom, targetDpi, pos) => {
		if (!pos || pos === 'none') return;

		const { tick: segMeters, count: segments, total: niceMeters } =
			computeScale(outW, scaleDenom, targetDpi);

		// px 換算
		const barPx = niceMeters / scaleDenom * 1000 / 25.4 * targetDpi;
		const segPx = barPx / segments;

		// 単位ラベル: tick (segMeters) が 1km の整数倍なら km 表示
		const useKm     = segMeters >= 1000 && Number.isInteger(segMeters / 1000);
		const unitLabel = useKm ? 'km' : 'm';
		const segLabel  = (v) => useKm ? `${v / 1000}` : `${v}`;

		// 余白・バーサイズ
		const margin  = targetDpi * 0.2;   // 約6mm
		const barH    = targetDpi * 0.05;  // 約1.5mm 横棒の高さ
		const fontSize = targetDpi * 0.08; // 約2.4mm

		// 配置
		const isTop    = pos.startsWith('top');
		const isRight  = pos.endsWith('right');
		const isCenter = pos.endsWith('center');

		let x0 = isRight  ? outW - margin - barPx
		       : isCenter ? (outW - barPx) / 2
		       :            margin;
		const y0 = isTop ? margin : outH - margin - barH - fontSize * 1.8;

		ctx.save();
		ctx.font         = `bold ${fontSize}px sans-serif`;
		ctx.textAlign    = 'center';
		ctx.textBaseline = 'top';
		ctx.lineWidth    = Math.max(1, targetDpi * 0.006);

		// 市松模様のセグメント描画
		for (let i = 0; i < segments; i++) {
			ctx.fillStyle = i % 2 === 0 ? '#000000' : '#ffffff';
			ctx.fillRect(x0 + i * segPx, y0, segPx, barH);
		}
		// 外枠
		ctx.strokeStyle = '#000000';
		ctx.strokeRect(x0, y0, barPx, barH);

		// 目盛りラベル: 区切り線の右側 (i = 1..segments)
		ctx.fillStyle = '#000000';
		for (let i = 1; i <= segments; i++) {
			const val = segMeters * i;
			ctx.fillText(segLabel(val), x0 + i * segPx, y0 + barH + fontSize * 0.2);
		}
		// 単位 (m / km)
		ctx.textAlign = 'left';
		ctx.fillText(unitLabel, x0 + barPx + fontSize * 0.4, y0 + barH * 0.1);

		ctx.restore();
	};

	// QRコードを出力キャンバスの右下に描画（白余白付き 2cm×2cm）
	// 戻り値: QRコード領域の { x, y, size } （著作権表示の配置に使用）
	const drawQRCode = (ctx, outW, outH, targetDpi, pos = 'br') => {
		if (pos === 'none') return null;
		const qrCanvas = qrCanvasRef.current?.querySelector('canvas');
		if (!qrCanvas) return null;

		// 2cm = 20mm をピクセルに変換
		const sizePx = Math.round(20 / 25.4 * targetDpi);
		// 白余白: QRコード周囲に均等に配置（QRコード自体のサイズ内に余白を含める）
		const padding = Math.round(sizePx * 0.12);
		const qrDrawSize = sizePx - padding * 2;

		// 配置（用紙端から5mm内側。プリンタの印刷可能領域から外れて切れるのを避ける）
		const margin = Math.round(5 / 25.4 * targetDpi);
		const x = pos === 'bl' ? margin : outW - sizePx - margin;
		const y = outH - sizePx - margin;

		// 角丸白背景
		const radius = Math.round(sizePx * 0.08);
		ctx.save();
		ctx.fillStyle = '#FFFFFF';
		ctx.beginPath();
		ctx.roundRect(x, y, sizePx, sizePx, radius);
		ctx.fill();
		// QRコード描画
		ctx.drawImage(qrCanvas, x + padding, y + padding, qrDrawSize, qrDrawSize);
		ctx.restore();

		return { x, y, size: sizePx, pos };
	};

	// 地図の著作権表示テキストを取得（MapLibreのattributionコントロールから）
	const getAttributionText = useCallback(() => {
		if (!map) return '';
		// MapLibreのattribution DOM要素からテキストを取得
		const attrEl = map.getContainer().querySelector('.maplibregl-ctrl-attrib-inner');
		if (attrEl) return attrEl.textContent || '';
		// フォールバック: 全ソースのattributionを収集
		const style = map.getStyle();
		if (!style || !style.sources) return '';
		const attrs = [];
		for (const src of Object.values(style.sources)) {
			if (src.attribution) attrs.push(src.attribution.replace(/<[^>]*>/g, ''));
		}
		return [...new Set(attrs)].join(' | ');
	}, [map]);

	// タイトル・所有者・メモを左上に描画（角丸白背景付き・1行に混在フォント）
	const drawTitleInfo = (ctx, outW, outH, targetDpi) => {
		const attrFontSize = Math.round(targetDpi * 0.11);
		const titleFontSize = Math.round(attrFontSize * 1.5);
		const memoFontSize = Math.round(attrFontSize * 1.3);
		const margin = Math.round(3 / 25.4 * targetDpi);
		const bgPad = Math.round(titleFontSize * 0.5);
		const bgRadius = Math.round(titleFontSize * 0.4);

		const titleFont = `bold ${titleFontSize}px sans-serif`;
		const memoFont = `${memoFontSize}px sans-serif`;

		// 描画セグメント: [{text, font, fontSize}]
		const segments = [];
		const titleParts = [title, ownerName].filter(Boolean);
		if (titleParts.length > 0) {
			segments.push({ text: titleParts.join(' / '), font: titleFont, fontSize: titleFontSize });
		}
		if (memo) {
			if (segments.length > 0) segments.push({ text: ' / ', font: memoFont, fontSize: memoFontSize });
			segments.push({ text: memo.replace(/\r?\n/g, ' '), font: memoFont, fontSize: memoFontSize });
		}
		if (segments.length === 0) return;

		// 全体幅を計算
		let totalWidth = 0;
		for (const seg of segments) {
			ctx.font = seg.font;
			totalWidth += ctx.measureText(seg.text).width;
		}
		const lineHeight = titleFontSize * 1.4;
		const bgW = totalWidth + bgPad * 2;
		const bgH = lineHeight + bgPad * 2;

		ctx.save();
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';

		// 角丸白背景を描画
		ctx.fillStyle = '#FFFFFF';
		ctx.globalAlpha = 0.85;
		ctx.beginPath();
		ctx.roundRect(margin, margin, bgW, bgH, bgRadius);
		ctx.fill();

		// セグメントごとに描画（ベースライン中央揃えで1行）
		ctx.fillStyle = '#000000';
		ctx.globalAlpha = 0.9;
		let x = margin + bgPad;
		const y = margin + bgPad + lineHeight / 2;
		for (const seg of segments) {
			ctx.font = seg.font;
			ctx.fillText(seg.text, x, y);
			x += ctx.measureText(seg.text).width;
		}

		ctx.restore();
	};

	// 右上に縮尺と用紙サイズを描画
	const drawScalePaperInfo = (ctx, outW, outH, targetDpi) => {
		const fontSize = Math.round(targetDpi * 0.11);
		const margin = Math.round(3 / 25.4 * targetDpi);
		const bgPad = Math.round(fontSize * 0.5);
		const bgRadius = Math.round(fontSize * 0.4);
		const lineHeight = fontSize * 1.4;

		const scaleText = `1/${scale.toLocaleString('en-US')}`;
		const orientText = orientation === 'landscape' ? '横' : '縦';
		const paperText = `${paperSize} ${orientText}`;
		const text = `${scaleText}  ${paperText}`;

		ctx.save();
		ctx.font = `bold ${fontSize}px sans-serif`;
		const textWidth = ctx.measureText(text).width;
		const bgW = textWidth + bgPad * 2;
		const bgH = lineHeight + bgPad * 2;
		const x = outW - margin - bgW;
		const y = margin;

		ctx.fillStyle = '#FFFFFF';
		ctx.globalAlpha = 0.85;
		ctx.beginPath();
		ctx.roundRect(x, y, bgW, bgH, bgRadius);
		ctx.fill();

		ctx.fillStyle = '#000000';
		ctx.globalAlpha = 0.9;
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
		ctx.fillText(text, x + bgPad, y + bgPad + lineHeight / 2);

		ctx.restore();
	};

	// 著作権表示をQRコードの左側に描画（角丸白背景付き）
	const drawAttribution = (ctx, outW, outH, targetDpi, qrRect) => {
		const text = getAttributionText();
		if (!text) return;

		const fontSize = Math.round(targetDpi * 0.05); // 約1.5mm。スケールバー (0.08) と重ならないよう小さめ。
		const lineHeight = fontSize * 1.3;
		const margin = Math.round(3 / 25.4 * targetDpi);
		const bgPad = Math.round(fontSize * 0.5); // 背景の内側余白
		const bgRadius = Math.round(fontSize * 0.4); // 角丸半径

		// QRコードの左側に配置
		const gap = Math.round(2 / 25.4 * targetDpi);
		const rightEdge = qrRect
			? qrRect.x - gap
			: outW - margin;
		const maxWidth = rightEdge - margin - bgPad * 2;

		ctx.save();
		ctx.font = `${fontSize}px sans-serif`;
		ctx.textAlign = 'right';
		ctx.textBaseline = 'bottom';

		// テキストを折り返し
		const words = text.split(/\s+/);
		const lines = [];
		let currentLine = '';
		for (const word of words) {
			const testLine = currentLine ? currentLine + ' ' + word : word;
			if (ctx.measureText(testLine).width > maxWidth && currentLine) {
				lines.push(currentLine);
				currentLine = word;
			} else {
				currentLine = testLine;
			}
		}
		if (currentLine) lines.push(currentLine);

		// 背景サイズを計算
		let textMaxW = 0;
		for (const line of lines) {
			textMaxW = Math.max(textMaxW, ctx.measureText(line).width);
		}
		const textBlockH = lines.length * lineHeight;
		const bgW = textMaxW + bgPad * 2;
		const bgH = textBlockH + bgPad * 2;

		// QRコードの下端に揃えて配置
		const bottomY = qrRect
			? qrRect.y + qrRect.size
			: outH - margin;
		const bgX = rightEdge - bgW;
		const bgY = bottomY - bgH;

		// 角丸白背景を描画
		ctx.fillStyle = '#FFFFFF';
		ctx.globalAlpha = 0.85;
		ctx.beginPath();
		ctx.roundRect(bgX, bgY, bgW, bgH, bgRadius);
		ctx.fill();

		// テキスト描画
		ctx.fillStyle = '#000000';
		ctx.globalAlpha = 0.8;
		for (let i = 0; i < lines.length; i++) {
			const y = bgY + bgPad + (i + 1) * lineHeight;
			ctx.fillText(lines[i], rightEdge - bgPad, y);
		}

		ctx.restore();
	};

	// ひし形アイコン(MARKER_ARRAY[0..5]) + ラベル（絵文字含む元の文字列そのまま）でアイコンを生成
	// makeIconImageWithNumber の絵文字分岐を回避するため、画像分岐相当を自前で実装
	const diamondImgCacheRef = useRef({});
	const loadDiamondImg = (iconNum) => {
		const cached = diamondImgCacheRef.current[iconNum];
		if (cached && cached.complete) return Promise.resolve(cached);
		const url = MARKER_ARRAY[iconNum];
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'Anonymous';
			img.onload = () => { diamondImgCacheRef.current[iconNum] = img; resolve(img); };
			img.onerror = (e) => reject(e);
			img.src = url;
		});
	};
	const renderDiamondIcon = async (textTop, textBottom, size, iconNum) => {
		const img = await loadDiamondImg(iconNum);
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		const offset = size * 0.2;
		const margin = size * 0.1;
		const color = (Marker._markerColors && Marker._markerColors[iconNum % 6]) || '#931c26';
		const center = size / 2;
		ctx.drawImage(img, offset, offset, size - offset * 2, size - offset * 2);

		const topResult = Marker.calcFontSize(ctx, size, margin, textTop, size / 7);
		ctx.fillStyle = color;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'bottom';
		ctx.font = `bold ${topResult.fontSize}px Arial`;
		ctx.lineWidth = size / 20;
		ctx.strokeStyle = '#ffffff';
		ctx.strokeText(topResult.text, center, topResult.fontSize + size / 20);
		ctx.fillStyle = color;
		ctx.fillText(topResult.text, center, topResult.fontSize + size / 20);

		const bottomResult = Marker.calcFontSize(ctx, size, margin, textBottom, size / 7);
		ctx.fillStyle = color;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'top';
		ctx.font = `bold ${bottomResult.fontSize}px Arial`;
		ctx.lineWidth = size / 20;
		ctx.strokeStyle = '#ffffff';
		ctx.strokeText(bottomResult.text, center, size - bottomResult.fontSize);
		ctx.fillStyle = color;
		ctx.fillText(bottomResult.text, center, size - bottomResult.fontSize);

		return canvas.toDataURL('image/png');
	};

	// 印刷ダイアログ表示中、live map のマーカー／ラベル表示を印刷設定どおりに
	// 一時プレビューする。ダイアログを閉じる or 設定変更時には自動で復元。
	// 対象: マーカーサイズ・マーカーアイコン種別・文字サイズ（ユーザーラベルと
	// グリッドラベル）。磁北線とカラーモードは live map には適用しない。
	// 実印刷中 (isPrinting=true) はプレビューを一時解除（print 側のグリッド
	// ラベル再スケールと衝突しないように）。
	useEffect(() => {
		if (!map || isPrinting) return;

		const sizeMul = markerSizeMode === 'small' ? 0.75
			: markerSizeMode === 'large' ? 1.5 : 1.0;
		const textScale = textSizeMode === 'small' ? 0.75
			: textSizeMode === 'large' ? 1.5 : 1.0;
		const gridLabelScale = textScale * 1.5;

		// --- 1) マーカー（サイズ + アイコン種別） ---
		// 注意: maplibre Marker は element 自体に transform: translate を当てて
		// 位置決めしているため、transform を上書きすると位置が壊れる。
		// また remakeMarker は非同期で element 再生成 + addToMap が必要なので
		// 副作用が大きい。ここでは <img> の width/height を CSS で変えて
		// 見た目サイズだけプレビューする（anchor の微小ズレは preview 用途では許容）。
		const markerRestores = [];
		(markers || []).forEach((mo) => {
			const mi = mo && mo.marker;
			if (!mi || !mi.marker) return;
			const el = mi.marker.getElement();
			if (!el) return;
			const origSrc = ('src' in el) ? el.src : null;
			const origDisplay = el.style.display;
			const origWidth = el.style.width;
			const origHeight = el.style.height;
			markerRestores.push({ el, origSrc, origDisplay, origWidth, origHeight });

			if (markerMode === 'none') {
				el.style.display = 'none';
				return;
			}
			// 赤ひし形モード: アイコン画像を差し替え
			if (markerMode === 'redDiamond' && 'src' in el) {
				const data = mi.data || {};
				const origIconNumber = (typeof data.iconNumber === 'number') ? data.iconNumber : 4;
				const target = (colorMode === 'mono-red')
					? 4 : (((origIconNumber % 6) + 6) % 6);
				if (data.iconDataUrl) {
					// 写真マーカーはひし形にしても中身が分からず意味がないため非表示にする
					el.style.display = 'none';
				} else {
					const textTop = data.name || '';
					const noteText = data.note ? data.note
						: (data.elevation != null ? data.elevation + 'm' : '');
					renderDiamondIcon(textTop, noteText, (mi.size * sizeMul) || 60, target)
						.then((u) => { el.src = u; })
						.catch(() => { el.src = MARKER_ARRAY[target]; });
				}
			}
			// サイズ: img の width/height を CSS で拡縮
			if (sizeMul !== 1.0) {
				// natural サイズは mi.size (= 60 等)。img なら naturalWidth/Height
				// に値が入っているはずだが、未ロードなどに備えて mi.size を優先
				const base = mi.size || el.naturalWidth || el.offsetWidth || 60;
				const newPx = base * sizeMul;
				el.style.width = newPx + 'px';
				el.style.height = newPx + 'px';
			}
		});

		// --- 2) ラベル（symbol レイヤの text-size） ---
		// ユーザーラベルは -user-label シンボルレイヤ描画（textScale）、
		// グリッドラベルは -grid-label / -grid-cell-label（gridLabelScale）
		const gridRestores = [];
		const allLayers = map.getStyle().layers || [];
		for (const layer of allLayers) {
			if (layer.type !== 'symbol') continue;
			// MGRS/UTM グリッドは独自にズーム連動フォントを持ち、印刷はキャンバス描画で
			// 別途処理するため、ここでの再スケール対象から除外（二重スケール巨大化を防ぐ）
			if (layer.id.startsWith('mgrs-utm-grid')) continue;
			const isUserLabel = layer.id.endsWith('-user-label');
			if (!isUserLabel && !layer.id.endsWith('-grid-label')
				&& !layer.id.endsWith('-grid-cell-label')) continue;
			const origSize = map.getLayoutProperty(layer.id, 'text-size');
			if (origSize == null) continue;
			gridRestores.push({ id: layer.id, origSize });
			map.setLayoutProperty(layer.id, 'text-size',
				['*', isUserLabel ? textScale : gridLabelScale, origSize]);
		}

		return () => {
			markerRestores.forEach(({ el, origSrc, origDisplay, origWidth, origHeight }) => {
				el.style.display = origDisplay;
				el.style.width = origWidth;
				el.style.height = origHeight;
				if (origSrc != null && 'src' in el) el.src = origSrc;
			});
			gridRestores.forEach(({ id, origSize }) => {
				try { map.setLayoutProperty(id, 'text-size', origSize); } catch (e) {}
			});
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, markers, markerSizeMode, markerMode,
		textSizeMode, colorMode, isPrinting]);

	// マーカーを出力キャンバスに描画
	// renderedBounds: capture() が返した実際の描画地理範囲 { west, east, north, south }
	// map.project() を使わず地理座標ベース（Mercator Y）で位置を計算するため
	// pixelRatio変更やCSS変換に影響されない
	const drawMarkersOnCanvas = useCallback((ctx, outW, outH, renderedBounds, targetDpi, textSizeScale = 1, sizeMultiplier = 1, skipPhotoMarkers = false) => {
		if (!markers || markers.length === 0) return;
		const { west, east, north, south } = renderedBounds;
		const lngSpan = east  - west;
		const nY = mercY(north);
		const sY = mercY(south);
		const mYSpan = nY - sY;
		if (lngSpan <= 0 || mYSpan <= 0) return;
		for (const markerObj of markers) {
			const markerInstance = markerObj.marker;
			if (!markerInstance || !markerInstance.marker) continue;
			// ひし形モードでは写真マーカーを描画しない（ひし形にしても中身が分からないため）
			if (skipPhotoMarkers && markerInstance.data && markerInstance.data.iconDataUrl) continue;
			const mlMarker = markerInstance.marker;
			const lngLat   = mlMarker.getLngLat();
			// 経度は線形、緯度は Mercator Y で非線形補間
			const outX = (lngLat.lng         - west) / lngSpan * outW;
			const outY = (nY - mercY(lngLat.lat))    / mYSpan  * outH;
			// 印刷物上でのアイコン物理サイズを 8mm 相当に固定（文字サイズ係数を適用）
			const iconMm   = (markerInstance.size || 60) / 60 * 10;//10mm を基準にサイズを調整
			const iconSize = iconMm / 25.4 * targetDpi * textSizeScale * sizeMultiplier;
			const element  = mlMarker.getElement();
			if (outX >= -iconSize && outX <= outW + iconSize &&
				outY >= -iconSize && outY <= outH + iconSize) {
				ctx.drawImage(element,
					outX - iconSize / 2, outY - iconSize / 2,
					iconSize, iconSize);
			}
		}
	}, [markers]);

	const drawUserLabelsOnCanvas = useCallback((ctx, outW, outH, renderedBounds, targetDpi, textSizeScale = 1) => {
		if (!map) return;
		const { west, east, north, south } = renderedBounds;
		const lngSpan = east - west;
		const nY = mercY(north);
		const sY = mercY(south);
		const mYSpan = nY - sY;
		if (lngSpan <= 0 || mYSpan <= 0) return;
		const style = map.getStyle();
		const labelFeatures = [];
		for (const sourceId of Object.keys((style && style.sources) || {})) {
			const src = map.getSource(sourceId);
			if (!src || !src._data) continue;
			(src._data.features || []).forEach(f => {
				if (f && f.properties && f.properties.userLabel === true) labelFeatures.push(f);
			});
		}
		for (const feature of labelFeatures) {
			const [lng, lat] = feature.geometry.coordinates;
			const props = feature.properties;
			const outX = (lng - west) / lngSpan * outW;
			const outY = (nY - mercY(lat)) / mYSpan * outH;
			// fontSize=20 → 約5mm 相当（文字サイズ係数を適用）
			const fontSizeMm = (props.fontSize || 20) / 20 * 5;//5mm を基準にサイズを調整
			const outFontSize = fontSizeMm / 25.4 * targetDpi * textSizeScale;
			ctx.save();
			ctx.font = `bold ${outFontSize}px sans-serif`;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.strokeStyle = props.haloColor || '#ffffff';
			ctx.lineWidth = outFontSize * 0.15;
			ctx.lineJoin = 'round';
			ctx.fillStyle = props.color || '#000000';
			// 複数行ラベル: 地図表示(text-line-height 1.2)と同じ行間で、全体の中心を座標に合わせる
			const lines = String(props.label || '').split('\n');
			const lineHeight = outFontSize * 1.2;
			const firstY = outY - (lines.length - 1) * lineHeight / 2;
			lines.forEach((line, i) => {
				ctx.strokeText(line, outX, firstY + i * lineHeight);
			});
			lines.forEach((line, i) => {
				ctx.fillText(line, outX, firstY + i * lineHeight);
			});
			ctx.restore();
		}
	}, [map]);

	// 印刷用キャンバスを生成する（「印刷」「PDF保存」共通の処理）。
	// レイヤ・線幅・ビュー・印刷枠の復元は成功・失敗にかかわらず finally で行う。
	// 戻り値: { canvas, bounds, widthMm, heightMm, printTitle }
	//   bounds はキャンバス全体に対応する地理範囲 (WGS84 度)
	const generatePrintCanvas = async () => {
		const { widthMm, heightMm } = getPaperDimensions();
		const printTitle = title || t('DefaultMapTitle');
		const bounds     = calculateBounds();

		savedViewRef.current = { center: map.getCenter(), zoom: map.getZoom() };
		let restoreLineWidths = () => {};
		const gridLabelLayerBackups = [];
		const userLabelLayerBackups = [];

		try {
			// 1. 印刷範囲の枠を非表示
			removePrintArea();

			// 2. 印刷解像度（300 dpi 相当）
			const targetDpi   = 300;
			const outWidthPx  = Math.round(widthMm  / 25.4 * targetDpi);
			const outHeightPx = Math.round(heightMm / 25.4 * targetDpi);

			// 2.5. 線密度に応じて線幅を自動調整（文字潰れ防止）
			try {
				const result = adjustLineWidthsForPrint(
					map, bounds, scale, widthMm, heightMm, outWidthPx, outHeightPx
				);
				restoreLineWidths = result.restore;
			} catch (e) {
				console.error('[PrintMap] adjustLineWidthsForPrint error:', e);
			}

			// === クラスタ化確認用 ===
			// let debugClusters = [];
			// try {
			// 	const result = adjustLineWidthsForPrint(
			// 		map, bounds, scale, widthMm, heightMm, outWidthPx, outHeightPx
			// 	);
			// 	restoreLineWidths = result.restore;
			// 	debugClusters = result.clusters || [];
			// 	console.log('[PrintMap] adjustLineWidthsForPrint done, clusters:', debugClusters.length);
			// } catch (e) {
			// 	console.error('[PrintMap] adjustLineWidthsForPrint error:', e);
			// }
			// if (debugClusters.length > 0) {
			// 	const circleFeatures = debugClusters.map((cl, i) => {
			// 		const pts = [];
			// 		for (let a = 0; a <= 32; a++) {
			// 			const theta = (a / 32) * Math.PI * 2;
			// 			pts.push([
			// 				cl.centerLng + cl.radiusLng * 1.2 * Math.cos(theta),
			// 				cl.centerLat + cl.radiusLat * 1.2 * Math.sin(theta),
			// 			]);
			// 		}
			// 		return {
			// 			type: 'Feature',
			// 			geometry: { type: 'LineString', coordinates: pts },
			// 			properties: { label: `C${i}(${cl.count}) f=${cl.factor.toFixed(2)}` },
			// 		};
			// 	});
			// 	map.addSource('_debug_clusters', {
			// 		type: 'geojson',
			// 		data: { type: 'FeatureCollection', features: circleFeatures },
			// 	});
			// 	map.addLayer({
			// 		id: '_debug_clusters_line',
			// 		type: 'line',
			// 		source: '_debug_clusters',
			// 		paint: { 'line-color': '#FF0000', 'line-width': 3, 'line-dasharray': [4, 2] },
			// 	});
			// 	map.addLayer({
			// 		id: '_debug_clusters_label',
			// 		type: 'symbol',
			// 		source: '_debug_clusters',
			// 		layout: {
			// 			'text-field': ['get', 'label'],
			// 			'text-size': 14,
			// 			'symbol-placement': 'point',
			// 			'text-anchor': 'center',
			// 		},
			// 		paint: { 'text-color': '#FF0000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 2 },
			// 	});
			// 	console.log('[PrintMap] debug cluster circles added to map');
			// }
			// === クラスタ化確認用ここまで ===

			// 2.7. グリッドラベルの text-size を調整（キャプチャ前）
			// デフォルト1.5倍 × 文字サイズ調整係数（常に適用）
			const textSizeScale = textSizeMode === 'small' ? 0.75 : textSizeMode === 'large' ? 1.5 : 1.0;
			const gridLabelScale = textSizeScale * 1.5;
			const allLayers = map.getStyle().layers || [];
			for (const layer of allLayers) {
				if (layer.type !== 'symbol') continue;
				// MGRS/UTM グリッドはここで再スケールしない（drawMgrsUtmGrid が印刷描画を担当）
				if (layer.id.startsWith('mgrs-utm-grid')) continue;
				if (!layer.id.endsWith('-grid-label') && !layer.id.endsWith('-grid-cell-label')) continue;
				const origSize = map.getLayoutProperty(layer.id, 'text-size');
				if (origSize == null) continue;
				gridLabelLayerBackups.push({ id: layer.id, origSize });
				map.setLayoutProperty(layer.id, 'text-size', ['*', gridLabelScale, origSize]);
			}

			// 2.8. ユーザーラベル／MGRS・UTMグリッドのレイヤを一時非表示（キャプチャ前）
			// これらは出力キャンバスに別途描画するため、地図キャンバス側に写ると二重描画になる
			for (const layer of allLayers) {
				if (layer.type === 'symbol' && layer.id.endsWith('-user-label')) {
					const origVisibility = map.getLayoutProperty(layer.id, 'visibility');
					userLabelLayerBackups.push({ id: layer.id, origVisibility });
					map.setLayoutProperty(layer.id, 'visibility', 'none');
				} else if (layer.id.startsWith('mgrs-utm-grid')) {
					const origVisibility = map.getLayoutProperty(layer.id, 'visibility');
					userLabelLayerBackups.push({ id: layer.id, origVisibility });
					map.setLayoutProperty(layer.id, 'visibility', 'none');
				}
			}

			// 3. キャプチャ（ポップアップ内のプログレスバーを更新）
			//    window.open はキャプチャ完了後に呼ぶ → ユーザーが地図タブを離れないため
			//    バックグラウンドタブによる WebGL 停止が起きない
			const printer = new MapTilePrinter(map);
			const { canvas: outCanvas, renderedBounds } = await printer.capture({
				bounds,
				outWidthPx,
				outHeightPx,
				tileGrid: 2,
				onProgress: (done, total) => setProgress(Math.round(done / total * 100)),
			});

			// 4. マーカーと磁北線を描画
			const outCtx = outCanvas.getContext('2d');
			if (renderedBounds) {
				if (markerMode !== 'none') {
					// ひし形モード: 一時的に各マーカーの画像をひし形アイコンに差し替え
					//   - モノ＋赤    → 全マーカー iconNumber=4 (赤ひし形)
					//   - カラー/モノ → 元の iconNumber を 6 で割った余り（色違いのひし形）
					const restoreMarkerSrcs = [];
					if (markerMode === 'redDiamond' && markers && markers.length > 0) {
						const swaps = markers.map((markerObj) => {
							const markerInstance = markerObj.marker;
							if (!markerInstance || !markerInstance.marker) return null;
							const element = markerInstance.marker.getElement();
							if (!element || !('src' in element)) return null;
							const originalSrc = element.src;
							restoreMarkerSrcs.push({ element, originalSrc });
							const data = markerInstance.data || {};
							const origIconNumber = (typeof data.iconNumber === 'number') ? data.iconNumber : 4;
							const targetIconNumber = (colorMode === 'mono-red') ? 4 : (((origIconNumber % 6) + 6) % 6);
							if (data.iconDataUrl) {
								// 写真マーカーはひし形にしても意味がないので印刷しない
								// （drawMarkersOnCanvas 側で描画をスキップする）
								return null;
							}
							// 通常/絵文字マーカー: ひし形画像 + 元のラベル（絵文字含む）で描画
							// makeIconImageWithNumber は名前先頭が絵文字だと画像分岐ではなく
							// 絵文字描画分岐に入ってしまうため、画像分岐相当の処理を独自に行う
							const textTop = data.name || '';
							const noteText = data.note ? data.note : (data.elevation != null ? data.elevation + 'm' : '');
							return renderDiamondIcon(textTop, noteText, markerInstance.size || 60, targetIconNumber).then((dataURL) => {
								return new Promise((resolve) => {
									element.onload = () => { element.onload = null; resolve(); };
									element.onerror = () => { element.onerror = null; resolve(); };
									element.src = dataURL;
								});
							}).catch(() => {
								element.src = MARKER_ARRAY[targetIconNumber];
							});
						}).filter(Boolean);
						await Promise.all(swaps);
					}
					try {
						const sizeMul = markerSizeMode === 'small' ? 0.75 : markerSizeMode === 'large' ? 1.5 : 1.0;
						drawMarkersOnCanvas(outCtx, outWidthPx, outHeightPx, renderedBounds, targetDpi, textSizeScale, sizeMul, markerMode === 'redDiamond');
					} finally {
						// 元のアイコン画像に復元
						for (const { element, originalSrc } of restoreMarkerSrcs) {
							element.src = originalSrc;
						}
					}
				}
				drawUserLabelsOnCanvas(outCtx, outWidthPx, outHeightPx, renderedBounds, targetDpi, textSizeScale);
			}
			const magneticColorHex = (MAGNETIC_LINE_COLORS.find(c => c.value === magneticLineColor) || MAGNETIC_LINE_COLORS[0]).color;
			drawMagneticLines(outCtx, outWidthPx, outHeightPx, bounds, lineCount, magneticColorHex);
			drawMgrsUtmGrid(outCtx, outWidthPx, outHeightPx, renderedBounds, targetDpi, textSizeScale);
			drawScaleBar(outCtx, outWidthPx, outHeightPx, scale, targetDpi, scaleBarPos);
			const qrRect = drawQRCode(outCtx, outWidthPx, outHeightPx, targetDpi, qrPos);
			drawAttribution(outCtx, outWidthPx, outHeightPx, targetDpi, qrRect && qrRect.pos === 'br' ? qrRect : null);
			drawTitleInfo(outCtx, outWidthPx, outHeightPx, targetDpi);
			drawScalePaperInfo(outCtx, outWidthPx, outHeightPx, targetDpi);

			// 4.5. カラーモード変換
			if (colorMode === 'mono' || colorMode === 'mono-red') {
				const imageData = outCtx.getImageData(0, 0, outWidthPx, outHeightPx);
				const d = imageData.data;
				if (colorMode === 'mono') {
					for (let i = 0; i < d.length; i += 4) {
						const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
						d[i] = d[i + 1] = d[i + 2] = gray;
					}
				} else {
					// モノ＋赤: 元の色を保持しつつグレー変換、赤成分が強いピクセルだけ元の色を復元
					const orig = new Uint8ClampedArray(d);
					for (let i = 0; i < d.length; i += 4) {
						const r = orig[i], g = orig[i + 1], b = orig[i + 2];
						const gray = 0.299 * r + 0.587 * g + 0.114 * b;
						if (r > 80 && r > g * 1.5 && r > b * 1.5) {
							// 赤ピクセル: 元の色を維持
							d[i] = r; d[i + 1] = g; d[i + 2] = b;
						} else {
							d[i] = d[i + 1] = d[i + 2] = gray;
						}
					}
				}
				outCtx.putImageData(imageData, 0, 0);
			}

			return { canvas: outCanvas, bounds, widthMm, heightMm, printTitle };
		} finally {
			// 5. レイヤ・線幅・ビュー・印刷枠を復元（成功・失敗共通）
			for (const { id, origSize } of gridLabelLayerBackups) {
				try { map.setLayoutProperty(id, 'text-size', origSize); } catch (e) {}
			}
			for (const { id, origVisibility } of userLabelLayerBackups) {
				try { map.setLayoutProperty(id, 'visibility', origVisibility == null ? 'visible' : origVisibility); } catch (e) {}
			}
			restoreLineWidths();
			if (savedViewRef.current) {
				map.setCenter(savedViewRef.current.center);
				map.setZoom(savedViewRef.current.zoom);
				savedViewRef.current = null;
			}
			updatePrintArea();
		}
	};

	const handlePrint = async () => {
		if (!map) return;

		setIsPrinting(true);
		setProgress(0);
		setPendingBlobUrl(null);
		try {
			const { canvas: outCanvas, widthMm, heightMm, printTitle } = await generatePrintCanvas();

			// 6. Blob URL を生成
			const marginMm = 3;
			const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${printTitle}</title>
<style>
@page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
html { margin: 0; padding: 0; }
body { margin: 0; padding: ${marginMm}mm; box-sizing: border-box; width: ${widthMm}mm; height: ${heightMm}mm; }
img { display: block; width: 100%; height: 100%; }
</style>
</head>
<body>
<img src="${outCanvas.toDataURL('image/jpeg', 0.8)}" onload="setTimeout(function(){window.print();},300)" />
</body>
</html>`;
			const blob    = new Blob([html], { type: 'text/html; charset=utf-8' });
			const blobUrl = URL.createObjectURL(blob);

			// 7. 印刷ウィンドウを開く
			//    キャプチャ完了後なのでユーザーは地図タブにいる。popup blocker で
			//    ブロックされた場合はポップアップ内にボタンを表示する。
			const printWindow = window.open(blobUrl, '_blank');
			if (!printWindow) {
				// ブロックされた場合: ポップアップ内にリンクボタンを表示
				setPendingBlobUrl(blobUrl);
			} else {
				setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
			}
		} catch (error) {
			console.error('Print error:', error);
		}
		setIsPrinting(false);
	};

	// エクスポート用ファイル名: タイトル_YYMMDD.拡張子 (不正文字は _ に置換)
	const makeExportFileName = (printTitle, ext) => {
		const now = new Date();
		const dateStr = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
		return `${printTitle.replace(/[\\/:*?"<>|]/g, '_')}_${dateStr}.${ext}`;
	};

	// GeoPDF (地理参照付きPDF) として保存
	const handleSavePdf = async () => {
		if (!map) return;

		setIsPrinting(true);
		setProgress(0);
		setPendingBlobUrl(null);
		try {
			const { canvas, bounds, widthMm, heightMm, printTitle } = await generatePrintCanvas();
			await exportGeoPdf({
				canvas,
				bounds,
				widthMm,
				heightMm,
				title: printTitle,
				fileName: makeExportFileName(printTitle, 'pdf'),
			});
		} catch (error) {
			console.error('PDF export error:', error);
		}
		setIsPrinting(false);
	};

	// GeoTIFF (JPEG圧縮・EPSG:3857) として保存
	const handleSaveGeoTiff = async () => {
		if (!map) return;

		setIsPrinting(true);
		setProgress(0);
		setPendingBlobUrl(null);
		try {
			const { canvas, bounds, printTitle } = await generatePrintCanvas();
			await exportGeoTiff({
				canvas,
				bounds,
				fileName: makeExportFileName(printTitle, 'tif'),
			});
		} catch (error) {
			console.error('GeoTIFF export error:', error);
		}
		setIsPrinting(false);
	};

	const openPendingWindow = () => {
		if (!pendingBlobUrl) return;
		const w = window.open(pendingBlobUrl, '_blank');
		if (w) {
			setTimeout(() => URL.revokeObjectURL(pendingBlobUrl), 60000);
			setPendingBlobUrl(null);
		}
	};

	// 用紙プレビューのサイズ計算
	const { widthMm, heightMm } = getPaperDimensions();
	const previewMaxW = 130;
	const previewMaxH = 80;
	const previewMinW = 70;
	const paperAR = widthMm / heightMm;
	let previewW, previewH;
	if (paperAR > previewMaxW / previewMaxH) {
		previewW = previewMaxW;
		previewH = Math.round(previewMaxW / paperAR);
	} else {
		previewH = previewMaxH;
		previewW = Math.round(previewMaxH * paperAR);
	}
	// 縦向き用紙でも内部テキスト（210×277mm 等）が収まるよう最小幅を確保
	if (previewW < previewMinW) previewW = previewMinW;

	const rangeW = ((widthMm  / 1000) * scale / 1000).toFixed(1);
	const rangeH = ((heightMm / 1000) * scale / 1000).toFixed(1);

	return (
		<Draggable nodeRef={popupNodeRef} position={popupPos} onStop={onDragStop} cancel="input,select,button,textarea,a,.close_button,.save-menu">
		<div ref={popupNodeRef} className="popup_opaque" style={{ minWidth: `${POPUP_MIN_WIDTH}px`, maxWidth: isTwoCol ? `${POPUP_MAX_WIDTH_TWO_COL}px` : `${POPUP_MAX_WIDTH_ONE_COL}px`, top: 'auto', left: 'auto', position: 'fixed', inset: 0, margin: 'auto', height: 'fit-content', maxHeight: '95vh', width: isTwoCol ? `min(96vw, ${POPUP_MAX_WIDTH_TWO_COL}px)` : 'fit-content', overflowY: 'auto', overflowX: 'hidden' }}>
			{/* 非表示QRコード（印刷用キャンバスソース） */}
			<div ref={qrCanvasRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
				{qrUrl && <QRCodeCanvas value={qrUrl} size={256} level="M" />}
			</div>
			<div className="popup-inner">
				<div className="popup-header">
					<h2>{t('PrintSettings')}</h2>
					<span className="close_button" onClick={onClose}>&times;</span>
				</div>

				{!isCollapsed && (<>
				{/* 用紙プレビュー＋印刷範囲 */}
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
					<div style={{
						width: `${previewW}px`,
						height: `${previewH}px`,
						border: '2px solid rgba(255,255,255,0.8)',
						backgroundColor: 'rgba(255,255,255,0.12)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: '11px',
						color: 'rgba(255,255,255,0.85)',
						lineHeight: '1.5',
						flexShrink: 0,
					}}>
						<span style={{ fontWeight: 'bold' }}>{paperSize}</span>
						<span>{orientation === 'landscape' ? t('Landscape_short') : t('Portrait_short')}</span>
						<span style={{ fontSize: '10px' }}>{widthMm}×{heightMm}mm</span>
					</div>
					<div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)', textAlign: 'left', lineHeight: '1.4' }}>
						{t('PrintRange')}<br />{rangeW} × {rangeH} km
					</div>
				</div>

				<div className={isTwoCol ? 'print-form-grid' : 'print-form-col'} style={isTwoCol ? { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '8px', rowGap: '2px' } : { display: 'flex', flexDirection: 'column' }}>

				<label style={{ marginBottom: '2px' }}>{t('PaperSize')}:
					<select value={paperSize} onChange={(e) => setPaperSize(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						{Object.keys(PAPER_SIZES).map(s => <option key={s} value={s}>{s}</option>)}
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('Orientation')}:
					<select value={orientation} onChange={(e) => setOrientation(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="landscape">{t('Landscape')}</option>
						<option value="portrait">{t('Portrait')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('Scale')}:
					<select
						value={isCustomScale ? 'custom' : scale}
						onChange={(e) => {
							if (e.target.value === 'custom') {
								setIsCustomScale(true);
								if (customScaleInput) {
									const v = parseInt(customScaleInput);
									if (v >= CUSTOM_SCALE_MIN && v <= CUSTOM_SCALE_MAX) setScale(v);
								}
							} else {
								setIsCustomScale(false);
								setScale(parseInt(e.target.value));
							}
						}}
						disabled={isPrinting}
						style={{ marginTop: '2px', padding: '3px 5px' }}
					>
						{SCALES.map(s => <option key={s.value} value={s.value}>{s.value === 'custom' ? t('CustomScale') : s.label}</option>)}
					</select>
				</label>
				<label style={{ marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px', alignSelf: 'end', width: '100%', opacity: isCustomScale ? 1 : 0.4 }}>
					<span style={{ whiteSpace: 'nowrap', fontSize: '16px' , marginTop: '-4px'}}>1:</span>
					<input
						type="number"
						min={CUSTOM_SCALE_MIN}
						max={CUSTOM_SCALE_MAX}
						step="100"
						value={customScaleInput}
						placeholder={t('CustomScaleRangeHint')}
						onChange={(e) => {
							setCustomScaleInput(e.target.value);
							const v = parseInt(e.target.value);
							if (v >= CUSTOM_SCALE_MIN && v <= CUSTOM_SCALE_MAX) setScale(v);
						}}
						disabled={isPrinting || !isCustomScale}
						style={{ flex: '1 1 0', minWidth: 0, padding: '3px 5px' }}
					/>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('ScaleBar')}:
					<select value={scaleBarPos} onChange={(e) => setScaleBarPos(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="none">{t('None')}</option>
						<option value="top-left">{t('TopLeft')}</option>
						<option value="top-center">{t('TopCenter')}</option>
						<option value="top-right">{t('TopRight')}</option>
						<option value="bottom-left">{t('BottomLeft')}</option>
						<option value="bottom-center">{t('BottomCenter')}</option>
						<option value="bottom-right">{t('BottomRight')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('TextSizeAdjust')}:
					<select value={textSizeMode} onChange={(e) => setTextSizeMode(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="small">{t('Size_Small')}</option>
						<option value="medium">{t('Size_Medium')}</option>
						<option value="large">{t('Size_Large')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('ColorMode')}:
					<select value={colorMode} onChange={(e) => setColorMode(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="color">{t('ColorMode_Color')}</option>
						<option value="mono">{t('ColorMode_Mono')}</option>
						<option value="mono-red">{t('ColorMode_MonoRed')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('PrintQRCode')}:
					<select value={qrPos} onChange={(e) => setQrPos(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="br">{t('BottomRight')}</option>
						<option value="bl">{t('BottomLeft')}</option>
						<option value="none">{t('None')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('MagneticNorthLine')}:
					<select value={lineCount} onChange={(e) => setLineCount(parseInt(e.target.value))} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value={0}>{t('None')}</option>
						{[2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n} {t('MagneticLinesUnit')}</option>)}
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('MagneticNorthLineColor')}:
					<select value={magneticLineColor} onChange={(e) => setMagneticLineColor(e.target.value)} disabled={isPrinting || lineCount === 0} style={{ marginTop: '2px', padding: '3px 5px' }}>
						{MAGNETIC_LINE_COLORS.map(c => <option key={c.value} value={c.value}>{t(c.label)}</option>)}
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('MarkerPrintMode')}:
					<select value={markerMode} onChange={(e) => setMarkerMode(e.target.value)} disabled={isPrinting} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="none">{t('MarkerPrintMode_None')}</option>
						<option value="normal">{t('MarkerPrintMode_Normal')}</option>
						<option value="redDiamond">{t('MarkerPrintMode_RedDiamond')}</option>
					</select>
				</label>

				<label style={{ marginBottom: '2px' }}>{t('MarkerSize')}:
					<select value={markerSizeMode} onChange={(e) => setMarkerSizeMode(e.target.value)} disabled={isPrinting || markerMode === 'none'} style={{ marginTop: '2px', padding: '3px 5px' }}>
						<option value="small">{t('Size_Small')}</option>
						<option value="medium">{t('Size_Medium')}</option>
						<option value="large">{t('Size_Large')}</option>
					</select>
				</label>

				</div>

				</>)}

				{/* 生成中プログレスバー */}
				{isPrinting && (
					<div style={{ marginBottom: '8px' }}>
						<div style={{ fontSize: '13px', marginBottom: '3px', color: 'rgba(255,255,255,0.9)' }}>
							{t('GeneratingMap')} {progress}%
						</div>
						<div style={{ width: '100%', height: '10px', background: 'rgba(255,255,255,0.2)', borderRadius: '5px', overflow: 'hidden' }}>
							<div style={{
								width: `${progress}%`,
								height: '100%',
								background: '#3af',
								borderRadius: '5px',
								transition: 'width 0.3s ease',
							}} />
						</div>
					</div>
				)}

				{/* popup blocker でブロックされた場合のボタン */}
				{pendingBlobUrl && (
					<div style={{ marginBottom: '10px', fontSize: '12px', color: 'rgba(255,255,200,0.9)', textAlign: 'left' }}>
						{t('PopupBlocked')}
						<button className="button_b" style={{ marginTop: '6px', width: '100%' }} onClick={openPendingWindow}>
							{t('OpenPrintPreview')}
						</button>
					</div>
				)}

				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
					<button className="button_b" onClick={onClose}>{t('Close')}</button>
					<button
						className="button_b"
						onClick={handleToggleCollapse}
						title={isCollapsed ? t('Expand') : t('Collapse')}
						aria-label={isCollapsed ? t('Expand') : t('Collapse')}
					>
						{isCollapsed ? '▼' : '▲'}
					</button>
					<div ref={saveMenuWrapRef} style={{ position: 'relative' }}>
						<button className="button_b" onClick={() => setShowSaveMenu(v => !v)} disabled={isPrinting}>
							{t('SaveMenu')} ▾
						</button>
						{showSaveMenu && (
							<div className="save-menu" style={isCollapsed ? { top: 'calc(100% + 6px)' } : { bottom: 'calc(100% + 6px)' }}>
								<div className="save-menu-item" onClick={() => { setShowSaveMenu(false); handleSavePdf(); }}>
									{t('SavePdf')}
								</div>
								<div className="save-menu-item" onClick={() => { setShowSaveMenu(false); handleSaveGeoTiff(); }}>
									{t('SaveGeoTiff')}
								</div>
							</div>
						)}
					</div>
					<button className="button_y" onClick={handlePrint} disabled={isPrinting}>
						{isPrinting ? t('Processing') : t('Print')}
					</button>
				</div>
			</div>
		</div>
		</Draggable>
	);
};

export default PrintMapPopup;
